"use client";

import { useEffect, useRef, useState } from "react";
import css from "./LivePhotoVideo.module.css";

export interface LivePhotoVideoProps {
  /** Proxied URL for the companion MOV. */
  readonly src: string;
  /** Stable identity for the underlying photo — when this changes, the
   *  video element remounts and replays from the start. */
  readonly photoId: string;
  /** When true, the video loops continuously. When toggled false, the
   *  current cycle is allowed to finish naturally. */
  readonly loop: boolean;
  /** Test hook only. Visible labelling comes from the surrounding group's
   *  aria-label (the photo alt) — the video itself is decorative. */
  readonly testId?: string;
}

/**
 * Live Photo overlay for the modal viewer. Autoplays once on mount
 * (muted, playsinline). Sits absolutely over the still image; once it
 * finishes a non-looping playthrough we hide the <video> so the JPEG
 * still beneath shows through — Apple Live Photos record ~1.5s on each
 * side of the still moment, so the video's first and last frames
 * intentionally differ from the still and the still is the canonical
 * "rest" frame.
 */
export function LivePhotoVideo({
  src,
  photoId,
  loop,
  testId = "live-photo-video",
}: LivePhotoVideoProps) {
  const ref = useRef<HTMLVideoElement | null>(null);
  // Hidden once a non-looping playthrough completes; revealed again when
  // the video plays (initial mount, or loop turning on after it ended).
  const [isHidden, setHidden] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (loop && video.ended) {
      setHidden(false);
      video.currentTime = 0;
      void video.play().catch(() => {
        /* play() may reject (autoplay denied); swallow. */
      });
    }
  }, [loop]);

  return (
    <video
      key={photoId}
      ref={ref}
      className={css.video}
      data-hidden={isHidden ? "" : undefined}
      src={src}
      autoPlay
      muted
      playsInline
      loop={loop}
      preload="auto"
      aria-hidden
      data-testid={testId}
      onEnded={() => {
        if (!loop) setHidden(true);
      }}
      onPlay={() => setHidden(false)}
    />
  );
}
